import { useQueryClient } from '@tanstack/react-query';
import {
  Database,
  LayoutDashboard,
  LineChart,
  Moon,
  Plus,
  Ruler,
  Settings,
  Sparkles,
  Sun,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useNavigation } from 'react-router-dom';

import { ErrorBoundary } from '../../../components/common/ErrorBoundary';
import { useAuth } from '../../../hooks/useAuth';
import { cn } from '../../../lib/cn';
import { useIsDesktop } from '../../../lib/useMediaQuery';
import {
  CommandPalette,
  Drawer,
  DrawerContent,
  Modal,
  ModalContent,
  notify,
  type CommandGroup,
} from '../../../ui';
import { AssistantPanel } from '../assistant/AssistantPanel';
import { useAssistant } from '../assistant/AssistantContext';
import { useApiSources, useCharts, useDashboards } from '../hooks';
import { familyForType } from '../lib/chartFamilies';
import { AccountMismatchGate } from './AccountMismatchGate';
import { ConnectionNotice } from './ConnectionNotice';
import { SidebarNav } from './SidebarNav';
import { TopBar } from './TopBar';
import { usePrefs } from './prefs';
import { SHORTCUT_HELP, useShortcuts } from './shortcuts';

/**
 * The application shell: navigation, breadcrumb, global search, the assistant,
 * and the routed surface.
 *
 * It replaced a single `AnalyticsPage` that held every screen behind a `view`
 * state variable — which meant no URL for anything, no browser back, no
 * bookmarks, and no way to link a colleague to a chart. Every surface is a route
 * now, and this component owns only what genuinely spans them.
 */
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const qc = useQueryClient();
  const isDesktop = useIsDesktop();
  const { user, logout } = useAuth();
  const { prefs, set: setPref, resolvedTheme } = usePrefs();
  const assistant = useAssistant();

  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { data: charts } = useCharts();
  const { data: dashboards } = useDashboards();
  const { data: sources } = useApiSources();

  const counts = {
    charts: charts?.length ?? 0,
    dashboards: dashboards?.length ?? 0,
    sources: sources?.length ?? 0,
  };

  /**
   * Manual refresh. Deliberately reloads the source list and dashboards too, not
   * just charts: an API deleted in the registry or a board built in the chatbot
   * lives in a different app's cache, and this is the one gesture that says
   * "catch up with the rest of the platform".
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['analytics', 'charts'] }),
        qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] }),
        qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] }),
        qc.invalidateQueries({ queryKey: ['analytics', 'webhooks'] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [qc]);

  const signOut = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  // ── keyboard ──────────────────────────────────────────────────────────────
  // `g` starts a short-lived "go to" chord, the convention every keyboard-driven
  // tool in this category uses (Linear, GitHub, Superhuman).
  const chord = useRef<{ key: string; at: number } | null>(null);

  const shortcuts = useMemo(
    () => [
      { key: 'k', meta: true, whileTyping: true, run: () => setSearchOpen(true) },
      { key: 'j', meta: true, whileTyping: true, run: () => assistant.toggle() },
      { key: 'b', meta: true, run: () => setPref('sidebarCollapsed', !prefs.sidebarCollapsed) },
      { key: '?', shift: true, run: () => setShortcutsOpen(true) },
      { key: 'c', run: () => navigate('/charts/new') },
      { key: 'd', run: () => navigate('/dashboards?new=1') },
      {
        key: 'g',
        run: () => {
          chord.current = { key: 'g', at: Date.now() };
        },
      },
      {
        key: 'h',
        run: () => {
          if (chord.current && Date.now() - chord.current.at < 1200) navigate('/');
          chord.current = null;
        },
      },
      {
        key: 's',
        run: () => {
          if (chord.current && Date.now() - chord.current.at < 1200) navigate('/data');
          chord.current = null;
        },
      },
    ],
    [assistant, navigate, prefs.sidebarCollapsed, setPref],
  );
  useShortcuts(shortcuts);

  // `g c` / `g d` need the chord to win over the bare `c` / `d` bindings above,
  // so they are checked first in their own handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!chord.current || Date.now() - chord.current.at > 1200) return;
      const key = e.key.toLowerCase();
      if (key !== 'c' && key !== 'd') return;
      const el = e.target;
      if (el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) {
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      chord.current = null;
      navigate(key === 'c' ? '/charts' : '/dashboards');
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [navigate]);

  // ── command palette ───────────────────────────────────────────────────────
  const groups = useMemo<CommandGroup[]>(() => {
    const actions: CommandGroup = {
      heading: 'Actions',
      items: [
        {
          id: 'new-chart',
          label: 'New chart',
          icon: Plus,
          shortcut: 'C',
          onSelect: () => navigate('/charts/new'),
        },
        {
          id: 'new-dashboard',
          label: 'New dashboard',
          icon: LayoutDashboard,
          shortcut: 'D',
          onSelect: () => navigate('/dashboards?new=1'),
        },
        {
          id: 'assistant',
          label: 'Ask the assistant',
          icon: Sparkles,
          shortcut: '⌘J',
          onSelect: () => assistant.setOpen(true),
        },
        {
          id: 'theme',
          label: resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
          icon: resolvedTheme === 'dark' ? Sun : Moon,
          onSelect: () => setPref('theme', resolvedTheme === 'dark' ? 'light' : 'dark'),
        },
        {
          id: 'metrics',
          label: 'Open metrics',
          icon: Ruler,
          keywords: ['metric', 'definition', 'glossary', 'semantic', 'measure'],
          onSelect: () => navigate('/metrics'),
        },
        {
          id: 'settings',
          label: 'Open settings',
          icon: Settings,
          onSelect: () => navigate('/settings'),
        },
      ],
    };

    return [
      actions,
      {
        heading: 'Charts',
        items: (charts ?? []).slice(0, 60).map((c) => ({
          id: `chart-${c.id}`,
          label: c.name,
          keywords: [c.chart_type, familyForType(c.chart_type).title],
          hint: c.chart_type.replace(/_/g, ' '),
          icon: LineChart,
          onSelect: () => navigate(`/charts/${c.id}`),
        })),
      },
      {
        heading: 'Dashboards',
        items: (dashboards ?? []).slice(0, 40).map((d) => ({
          id: `dash-${d.id}`,
          label: d.name,
          keywords: ['dashboard', 'board'],
          icon: LayoutDashboard,
          onSelect: () => navigate(`/dashboards/${d.id}`),
        })),
      },
      {
        heading: 'Data sources',
        items: (sources ?? []).slice(0, 40).map((s) => ({
          id: `src-${s.id}`,
          label: s.name,
          keywords: [s.kind ?? 'api', 'source'],
          hint: s.kind === 'table' ? 'table' : s.kind === 'webhook' ? 'webhook' : 'API',
          icon: Database,
          onSelect: () => navigate(`/data?source=${encodeURIComponent(s.id)}`),
        })),
      },
    ];
  }, [charts, dashboards, sources, navigate, assistant, resolvedTheme, setPref]);

  // Breadcrumb names for id segments in the URL.
  const names = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const id = parts[1];
    if (parts[0] === 'charts' && id) return { chart: (charts ?? []).find((c) => c.id === id)?.name };
    if (parts[0] === 'dashboards' && id) {
      return { dashboard: (dashboards ?? []).find((d) => d.id === id)?.name };
    }
    return {};
  }, [location.pathname, charts, dashboards]);

  const navigating = navigation.state === 'loading';

  return (
    <div data-app-shell className="flex h-dvh w-full overflow-hidden bg-bg text-fg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-100 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-xs focus:font-medium focus:text-primary-fg"
      >
        Skip to content
      </a>

      {/* Sidebar — fixed on desktop, a drawer below `lg`. */}
      {isDesktop ? (
        <SidebarNav
          collapsed={prefs.sidebarCollapsed}
          onToggleCollapsed={() => setPref('sidebarCollapsed', !prefs.sidebarCollapsed)}
          counts={counts}
          onNewChart={() => navigate('/charts/new')}
        />
      ) : (
        <Drawer open={navOpen} onOpenChange={setNavOpen}>
          <DrawerContent
            side="left"
            width="16rem"
            title="Navigation"
            className="[&>div:first-child]:hidden"
            bodyClassName="p-0"
          >
            <SidebarNav
              variant="drawer"
              collapsed={false}
              counts={counts}
              onNavigate={() => setNavOpen(false)}
              onNewChart={() => {
                setNavOpen(false);
                navigate('/charts/new');
              }}
            />
          </DrawerContent>
        </Drawer>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          user={user}
          names={names}
          onOpenNav={() => setNavOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onToggleAssistant={assistant.toggle}
          assistantOpen={assistant.open}
          onRefresh={() => void refresh()}
          refreshing={refreshing}
          onSignOut={() => void signOut()}
        />

        <ConnectionNotice />

        {/*
          Mounted for the whole signed-in app, not on the screen that happened to
          make the call. A refusal to act for this person at Data Studio surfaces
          while saving a table, while a board loads its tiles, and while the
          sources list refreshes — one condition wearing three disguises, and it
          used to be explained differently in each.
        */}
        <AccountMismatchGate />

        {/* Route transition indicator — a lazily-loaded surface is a real wait. */}
        <div
          aria-hidden
          className={cn(
            'h-0.5 shrink-0 origin-left bg-accent transition-transform duration-300',
            navigating ? 'scale-x-100' : 'scale-x-0',
          )}
        />

        <div className="flex min-h-0 flex-1">
          <main id="main" className="flex min-w-0 flex-1 flex-col">
            {/* Scoped per route: one broken surface must not blank the shell. */}
            <ErrorBoundary key={location.pathname} subject="This screen">
              <Outlet />
            </ErrorBoundary>
          </main>

          <AssistantPanel />
        </div>
      </div>

      <CommandPalette
        open={searchOpen}
        onOpenChange={(open) => {
          setSearchOpen(open);
          if (!open) setSearchQuery('');
        }}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        groups={groups}
        emptyMessage={
          <>
            Nothing matches <span className="font-medium text-fg-secondary">“{searchQuery}”</span>. Try a
            chart name, a dashboard, or a data source.
          </>
        }
      />

      <Modal open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <ModalContent
          title="Keyboard shortcuts"
          description="Every one of these is also reachable with the mouse."
          size="lg"
        >
          <div className="flex flex-col gap-5">
            {SHORTCUT_HELP.map((group) => (
              <section key={group.group}>
                <h3 className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-fg-faint">
                  {group.group}
                </h3>
                <ul className="flex flex-col gap-1.5">
                  {group.items.map((item) => (
                    <li key={item.label} className="flex items-center justify-between gap-4 text-[0.8125rem]">
                      <span className="text-fg-secondary">{item.label}</span>
                      <span className="flex shrink-0 items-center gap-1">
                        {item.keys.map((k) => (
                          <kbd
                            key={k}
                            className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-surface-hover px-1.5 font-mono text-[0.6875rem] font-medium text-fg-muted"
                          >
                            {k}
                          </kbd>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}

/**
 * A workspace-wide refresh, exposed for screens that need to trigger it after an
 * action taken elsewhere in the platform.
 */
export function useWorkspaceRefresh() {
  const qc = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['analytics', 'charts'] }),
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] }),
      qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] }),
    ]);
    notify.success('Workspace refreshed');
  }, [qc]);
}
